import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Gauge, Fuel, Zap, ChevronRight, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { getOptimizedImageUrl } from '../utils/images';
import { carPathToken } from '../utils/slugs';

const CarCard = ({ car, index = 0, onBeforeNavigate }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const financingValue = String(car.financingUntil || '').replace(/\D/g, '') || car.financingUntil;
  const hasFinancing = Number(financingValue) > 0;

  const getImageUrl = () => {
    if (!car.images || car.images.length === 0) return 'https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?w=800&q=80';
    const img = car.images[0];
    return typeof img === 'string' ? img : img.url;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      className="group relative bg-[#111111] rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-black/40"
    >
      {/* Full card link overlay */}
      <Link
        to={`/viatura/${carPathToken(car)}`}
        className="absolute inset-0 z-10"
        data-testid={`car-card-link-${car.id}`}
        aria-label={`${car.brand} ${car.model}`}
        onMouseDown={() => { if (onBeforeNavigate) onBeforeNavigate(); }}
        onTouchStart={() => { if (onBeforeNavigate) onBeforeNavigate(); }}
      />

      {/* Badge — oculto quando reservado ou vendido */}
      {car.badge && car.availability === 'available' && (
        <div className="absolute top-4 left-4 z-20 pointer-events-none">
          <span
            className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase text-white backdrop-blur-sm"
            style={{ backgroundColor: `${car.badgeColor}cc` }}
          >
            {car.badge}
          </span>
        </div>
      )}

      {/* Availability badge */}
      {car.availability === 'reserved' && (
        <div className="absolute top-4 right-4 z-20 pointer-events-none">
          <span className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase text-white bg-amber-500/90 backdrop-blur-sm">
            Reservado
          </span>
        </div>
      )}
      {car.availability === 'sold' && (
        <div className="absolute top-4 right-4 z-20 pointer-events-none">
          <span className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase text-white bg-red-600/90 backdrop-blur-sm">
            Vendido
          </span>
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <div className={`absolute inset-0 bg-[#1a1a1a] animate-pulse ${imageLoaded ? 'hidden' : ''}`} />
        <img
          src={getOptimizedImageUrl(getImageUrl(), 800)}
          alt={`${car.brand} ${car.model}`}
          width="800"
          height="500"
          loading={index < 2 ? 'eager' : 'lazy'}
          decoding="async"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${car.availability === 'sold' ? 'grayscale opacity-60' : ''}`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-60" />
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="text-white/40 text-xs font-medium tracking-wider uppercase">{car.brand}</p>
            <h3 className="text-white font-bold text-lg leading-tight mt-0.5">{car.model}</h3>
            {car.version && (
              <p className="mt-1 text-white font-bold text-sm leading-tight line-clamp-1">{car.version}</p>
            )}
          </div>
        </div>

        {/* Specs Grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center gap-2 text-white/40">
            <Calendar size={13} />
            <span className="text-xs">{car.date}</span>
          </div>
          <div className="flex items-center gap-2 text-white/40">
            <Gauge size={13} />
            <span className="text-xs">{car.mileageDisplay}</span>
          </div>
          <div className="flex items-center gap-2 text-white/40">
            <Zap size={13} />
            <span className="text-xs">{car.horsepower} cv</span>
          </div>
          <div className="flex items-center gap-2 text-white/40">
            <Fuel size={13} />
            <span className="text-xs">{car.fuel}</span>
          </div>
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div>
            <p className="text-[#C41E3A] font-bold text-xl">{car.priceDisplay}</p>
            {hasFinancing && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C41E3A]/20 bg-[#C41E3A]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/45">
                <CreditCard size={11} className="text-[#C41E3A]" />
                <span>Desde</span>
                <span className="text-white/75">{financingValue}€</span>
              </div>
            )}
          </div>
          <span className="flex items-center gap-1 text-white/50 text-xs font-medium group-hover:text-white transition-colors">
            Saber mais
            <ChevronRight size={14} className="transform group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default CarCard;
